import { ContextData, ContextDataItem } from '../types/widget';

/**
 * Check if an item should be included based on selection/hidden state.
 * - Hidden items are always included (they're auto-included, not user-toggleable)
 * - Non-hidden items are only included if selected
 */
export const shouldIncludeItem = (item: ContextDataItem): boolean => {
  return item.hidden || Boolean(item.selected);
};

/**
 * Build a record from context children, preserving original keys
 */
const buildChildrenRecord = (
  children: ContextDataItem[]
): Record<string, unknown> | undefined => {
  const record: Record<string, unknown> = {};
  let hasContent = false;

  for (const child of children) {
    if (!shouldIncludeItem(child)) continue;

    const key = String(child.key);
    if (
      child.value !== undefined &&
      child.value !== null &&
      child.value !== ''
    ) {
      record[key] = child.value;
      hasContent = true;
    }
  }

  return hasContent ? record : undefined;
};

/** Context keys emitted in unstructured context rather than structured placeholders. */
const EXCLUDED_KEYS = ['freeTextContext', 'patientDemographics'];

/**
 * Convert ContextData array to structured context format.
 *
 * This is a generic pass-through that preserves the original keys from the host.
 * Items with children are converted to nested objects. Items with direct values
 * are included as-is.
 *
 * Only items that are selected (or hidden with values) are included.
 * Free text context is excluded as it belongs in unstructured context.
 *
 * @param contextData - The context data array from the widget
 * @returns Structured context preserving original keys, or undefined if no relevant data
 */
export const buildStructuredContext = (
  contextData: ContextData
): Record<string, unknown> | undefined => {
  const structured: Record<string, unknown> = {};
  let hasContent = false;

  for (const item of contextData) {
    if (!shouldIncludeItem(item)) continue;

    const key = String(item.key);

    if (EXCLUDED_KEYS.includes(key)) continue;

    if (item.children && item.children.length > 0) {
      const childRecord = buildChildrenRecord(item.children);
      if (childRecord) {
        structured[key] = childRecord;
        hasContent = true;
      }
    } else if (
      item.value !== undefined &&
      item.value !== null &&
      item.value !== ''
    ) {
      structured[key] = item.value;
      hasContent = true;
    }
  }

  return hasContent ? structured : undefined;
};
